/**
 * Join step two: how the participant is willing to travel.
 *
 * The engine plans one route per accepted mode and keeps the fastest, so an
 * empty selection would leave it nothing to plan with; at least one mode is
 * therefore required before the wizard advances.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { TRAVEL_MODES, TRAVEL_MODE_DEFINITIONS, type TravelMode } from '@shared/travel'

import { ErrorAlert } from '../../../components/layout/states'
import { WizardFooter } from '../../../components/layout/wizard'
import { Card, CardContent } from '../../../components/ui/card'
import { Chip, ChipGroup } from '../../../components/ui/chip'
import { modeLabelKey } from '../../../lib/format'
import { useSharedOutlet } from '../shared-layout'
import { joinStepNeighbours, joinStepPath, useJoinDraft } from './layout'

export function JoinModesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { shareToken } = useSharedOutlet()
  const { draft, update } = useJoinDraft(shareToken)
  const { previous, next } = joinStepNeighbours('modes')

  const [error, setError] = useState<string | null>(null)

  function toggle(mode: TravelMode): void {
    const selected = new Set(draft.modes)
    if (selected.has(mode)) {
      selected.delete(mode)
    } else {
      selected.add(mode)
    }
    // Keep the catalog order so the draft does not depend on click order.
    update({ modes: TRAVEL_MODES.filter((candidate) => selected.has(candidate)) })
    setError(null)
  }

  function goNext(): void {
    if (draft.modes.length === 0) {
      setError(t('join.modesRequired'))
      return
    }
    if (next) {
      navigate(joinStepPath(shareToken, next))
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="flex flex-col gap-3 pt-4 sm:pt-5">
          <p className="text-sm">{t('join.modesHint')}</p>

          <ChipGroup label={t('join.stepModes')}>
            {TRAVEL_MODES.map((mode) => (
              <Chip
                key={mode}
                selected={draft.modes.includes(mode)}
                onClick={() => toggle(mode)}
              >
                <span aria-hidden>{TRAVEL_MODE_DEFINITIONS[mode].icon}</span>
                <span>{t(modeLabelKey(mode))}</span>
              </Chip>
            ))}
          </ChipGroup>

          {error ? (
            <ErrorAlert message={error} />
          ) : null}
        </CardContent>
      </Card>

      <WizardFooter
        backTo={previous ? joinStepPath(shareToken, previous) : undefined}
        backLabel={t('common.back')}
        onNext={goNext}
        nextLabel={t('common.next')}
      />
    </div>
  )
}
